// FTEDN — Beta program helpers (eligibility, timeline, feedback validation)
import type { BetaProgram, BetaApplication } from '../../stores/beta.store';

export type EligibilityResult = { eligible: true } | { eligible: false; reason: string };

/** Check if a user is eligible to apply for a beta program */
export function checkEligibility(
  program: BetaProgram,
  applications: BetaApplication[],
  userLevel: number,
): EligibilityResult {
  // Already applied?
  const existing = applications.find(a => a.programId === program.id);
  if (existing) {
    return { eligible: false, reason: `Already ${existing.status}` };
  }

  // Program not accepting?
  if (program.status !== 'accepting') {
    return { eligible: false, reason: 'Not accepting applications' };
  }

  // No spots left?
  if (program.spotsLeft <= 0) {
    return { eligible: false, reason: 'No spots remaining' };
  }

  // Level requirement (programs need at least level 2)
  if (userLevel < 2) {
    return { eligible: false, reason: 'Reach Level 2 to apply' };
  }

  return { eligible: true };
}

export interface ShipmentStep {
  label: string;
  done: boolean;
  date: string;
  current: boolean;
}

/** Build a shipment timeline from a raw shipment doc */
export function buildShipmentTimeline(
  raw: Record<string, unknown> | null
): ShipmentStep[] {
  if (!raw) {
    return [
      { label: 'Preparing', done: false, date: '', current: true },
      { label: 'Shipped', done: false, date: '', current: false },
      { label: 'In Transit', done: false, date: '', current: false },
      { label: 'Delivered', done: false, date: '', current: false },
    ];
  }

  const status = String(raw.status ?? 'preparing');
  const stages = ['preparing', 'shipped', 'in_transit', 'delivered'];
  const stageLabels: Record<string, string> = {
    preparing: 'Preparing',
    shipped: 'Shipped',
    in_transit: 'In Transit',
    delivered: 'Delivered',
  };
  const events = Array.isArray(raw.events) ? raw.events as Array<Record<string, unknown>> : [];

  const currentIdx = stages.indexOf(status);

  return stages.map((stage, i) => ({
    label: stageLabels[stage],
    done: i <= currentIdx,
    date: String(
      events.find(event => event.status === stage)?.timestamp
      ?? (raw as Record<string, unknown>)[`${stage}Date`]
      ?? (raw as Record<string, unknown>)[`${stage}At`]
      ?? '',
    ),
    current: i === currentIdx,
  }));
}

export type FeedbackType = 'thoughts' | 'review' | 'notes';

export interface FeedbackPayload {
  type: FeedbackType;
  content: string;
  programId: string;
  rating?: number;
}

/** Validate feedback before submission */
export function validateFeedback(payload: FeedbackPayload): { valid: true } | { valid: false; error: string } {
  if (!payload.content.trim()) {
    return { valid: false, error: 'Please enter your feedback' };
  }

  if (payload.content.trim().length < 10) {
    return { valid: false, error: 'Feedback must be at least 10 characters' };
  }

  if (payload.content.length > 5000) {
    return { valid: false, error: 'Feedback must be under 5000 characters' };
  }

  if (payload.type === 'review' && payload.rating !== undefined) {
    if (payload.rating < 1 || payload.rating > 5 || !Number.isInteger(payload.rating)) {
      return { valid: false, error: 'Rating must be 1-5' };
    }
  }

  if (!payload.programId) {
    return { valid: false, error: 'Program ID is required' };
  }

  return { valid: true };
}

/** Calculate tester score (0-100) from review stats */
export function calculateTesterScore(stats: {
  reviewCount: number;
  avgRating?: number;
  onTimePercent?: number;
  streak: number;
}): number {
  // Weight: reviews (40%), quality (30%), timeliness (20%), streak (10%)
  const reviewScore = Math.min(stats.reviewCount * 10, 40);
  const qualityScore = stats.avgRating ? (stats.avgRating / 5) * 30 : 15;
  const timeScore = stats.onTimePercent !== undefined ? (stats.onTimePercent / 100) * 20 : 10;
  const streakScore = Math.min(stats.streak, 10);

  return Math.round(Math.min(reviewScore + qualityScore + timeScore + streakScore, 100));
}
